import { useEffect, useState } from "react";
import StepProgress from "../components/website-Generator/StepProgress";
import BusinessDetailsStep from "../components/website-Generator/BusinessDetailsStep";
import ThemeSelectionStep from "../components/website-Generator/ThemeSelectionStep";
import ReviewStep from "../components/website-Generator/ReviewStep";
import ActionBar from "../components/website-Generator/ActionBar";
import GeneratedWebsiteExperience from "../components/website-Generator/GeneratedWebsiteExperience";

import {
    createAndQueueProject,
    getPreview,
    publishProject,
    regenerateProject,
    autofillWebsiteForm,
    getThemes
} from "../services/websiteService";

import "./GenerateWebsitePage.css";

const fallbackThemes = [
    { key: "clean-light", name: "Clean Light", description: "Modern clean business layout" },
    { key: "modern-glass", name: "Modern Glass", description: "Glassmorphism futuristic UI" },
    { key: "premium-dark", name: "Premium Dark", description: "Dark luxury professional layout" },
    { key: "luxury-light", name: "Luxury Light", description: "Elegant light premium feel" },
    { key: "bold-gradient", name: "Bold Gradient", description: "Vibrant modern tech UI" }
];

const defaultRecommendedPages = ["Home", "About", "Services", "Contact"];

const createEmptyOfferedItem = () => ({
    id: crypto.randomUUID(),
    name: "",
    subtitle: "",
    description: "",
    badge: "",
    category: "",
    audience: "",
    level: "",
    imagePreference: "",
    keyPoints: [""]
});

const normalizeOfferedItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
        return [createEmptyOfferedItem()];
    }

    return items.map((item) => ({
        id: item.id || crypto.randomUUID(),
        name: item.name || "",
        subtitle: item.subtitle || "",
        description: item.description || "",
        badge: item.badge || "",
        category: item.category || "",
        audience: item.audience || "",
        level: item.level || "",
        imagePreference: item.imagePreference || "",
        keyPoints: Array.isArray(item.keyPoints) && item.keyPoints.length > 0
            ? item.keyPoints.map((x) => x || "")
            : [""]
    }));
};

export default function GenerateWebsitePage() {
    const [formMode, setFormMode] = useState("fast");
    const [showOfferings, setShowOfferings] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [step, setStep] = useState(1);
    const [themes, setThemes] = useState(fallbackThemes);

    const [loading, setLoading] = useState(false);
    const [autofilling, setAutofilling] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [regenerating, setRegenerating] = useState(false);

    const [projectId, setProjectId] = useState("");
    const [status, setStatus] = useState("");
    const [liveUrl, setLiveUrl] = useState("");
    const [previewData, setPreviewData] = useState(null);
    const [variantKey, setVariantKey] = useState("A");

    const [selectedVariant, setSelectedVariant] = useState("A");
    const [previewRefreshKey, setPreviewRefreshKey] = useState(0);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.shrianshnexus.in";

    const refreshPreview = () => {
        setPreviewRefreshKey(prev => prev + 1);
    };

    const previewDocumentUrl = projectId
        ? `${API_BASE_URL}/api/website/${projectId}/preview-document?v=${selectedVariant}&t=${Date.now()}`
        : "";

    const [form, setForm] = useState({
        businessName: "",
        industry: "",
        subIndustry: "",
        description: "",

        tone: "Professional",
        themeKey: "",

        primaryAudience: "",
        secondaryAudience: "",
        location: "",
        serviceAreas: "",

        goalOfWebsite: "",
        callToActionType: "",
        brandStyle: "",
        pricePositioning: "",

        coreServices: "",
        projectTypes: "",
        uniqueSellingPoints: "",
        trustSignals: "",
        yearsInBusiness: "",
        mustIncludeWords: "",
        avoidWords: "",

        competitorWebsiteUrl: "",
        competitorStyleNotes: "",
        desiredLayoutStyle: "",
        visualTone: "",
        preferredSectionDensity: "",
        referenceWebsiteType: "",

        businessModel: "",
        primaryWebsiteMode: "",
        primaryEntityType: "",
        locationFocus: "",
        contentStyleHint: "",

        pages: defaultRecommendedPages,
        offeredItems: [createEmptyOfferedItem()]
    });

    useEffect(() => {
        const loadThemes = async () => {
            try {
                const res = await getThemes();
                if (Array.isArray(res) && res.length > 0) {
                    setThemes(res);
                }
            } catch {
                setThemes(fallbackThemes);
            }
        };

        loadThemes();
    }, []);

    const isValidThemeKey = (key, themeList) => {
        return themeList.some((t) => t.key === key);
    };

    const updateField = (name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const hasMeaningfulOfferedItems = (items) => {
        if (!Array.isArray(items) || items.length === 0) return false;

        return items.some((item) =>
            item?.name?.trim() ||
            item?.subtitle?.trim() ||
            item?.description?.trim() ||
            item?.badge?.trim() ||
            item?.category?.trim() ||
            item?.audience?.trim() ||
            item?.level?.trim() ||
            item?.imagePreference?.trim() ||
            (Array.isArray(item?.keyPoints) && item.keyPoints.some((x) => x?.trim()))
        );
    };

    const mergeValue = (prevValue, aiValue) => {
        return prevValue && String(prevValue).trim() !== ""
            ? prevValue
            : (aiValue || prevValue);
    };

    const mergeAutofillData = (data) => {
        setForm((prev) => {
            const userCustomizedPages =
                Array.isArray(prev.pages) &&
                prev.pages.length > 0 &&
                JSON.stringify(prev.pages) !== JSON.stringify(defaultRecommendedPages);

            const userHasOfferedItems = hasMeaningfulOfferedItems(prev.offeredItems);

            return {
                ...prev,
                businessName: mergeValue(prev.businessName, data.businessName),
                industry: mergeValue(prev.industry, data.industry),
                subIndustry: mergeValue(prev.subIndustry, data.subIndustry),
                description: mergeValue(prev.description, data.description),

                tone: mergeValue(prev.tone, data.tone),
                themeKey: isValidThemeKey(data.themeKey, themes)
                    ? mergeValue(prev.themeKey, data.themeKey)
                    : prev.themeKey,

                primaryAudience: mergeValue(prev.primaryAudience, data.primaryAudience),
                secondaryAudience: mergeValue(prev.secondaryAudience, data.secondaryAudience),
                location: mergeValue(prev.location, data.location),
                serviceAreas: mergeValue(prev.serviceAreas, data.serviceAreas),

                goalOfWebsite: mergeValue(prev.goalOfWebsite, data.goalOfWebsite),
                callToActionType: mergeValue(prev.callToActionType, data.callToActionType),
                brandStyle: mergeValue(prev.brandStyle, data.brandStyle),
                pricePositioning: mergeValue(prev.pricePositioning, data.pricePositioning),

                coreServices: mergeValue(prev.coreServices, data.coreServices),
                projectTypes: mergeValue(prev.projectTypes, data.projectTypes),
                uniqueSellingPoints: mergeValue(prev.uniqueSellingPoints, data.uniqueSellingPoints),
                trustSignals: mergeValue(prev.trustSignals, data.trustSignals),
                yearsInBusiness: mergeValue(prev.yearsInBusiness, data.yearsInBusiness),
                mustIncludeWords: mergeValue(prev.mustIncludeWords, data.mustIncludeWords),
                avoidWords: mergeValue(prev.avoidWords, data.avoidWords),

                competitorWebsiteUrl: mergeValue(prev.competitorWebsiteUrl, data.competitorWebsiteUrl),
                competitorStyleNotes: mergeValue(prev.competitorStyleNotes, data.competitorStyleNotes),
                desiredLayoutStyle: mergeValue(prev.desiredLayoutStyle, data.desiredLayoutStyle),
                visualTone: mergeValue(prev.visualTone, data.visualTone),
                preferredSectionDensity: mergeValue(prev.preferredSectionDensity, data.preferredSectionDensity),
                referenceWebsiteType: mergeValue(prev.referenceWebsiteType, data.referenceWebsiteType),

                businessModel: mergeValue(prev.businessModel, data.businessModel),
                primaryWebsiteMode: mergeValue(prev.primaryWebsiteMode, data.primaryWebsiteMode),
                primaryEntityType: mergeValue(prev.primaryEntityType, data.primaryEntityType),
                locationFocus: mergeValue(prev.locationFocus, data.locationFocus),
                contentStyleHint: mergeValue(prev.contentStyleHint, data.contentStyleHint),

                pages: userCustomizedPages
                    ? prev.pages
                    : (Array.isArray(data.pages) && data.pages.length > 0 ? data.pages : prev.pages),

                offeredItems: userHasOfferedItems
                    ? prev.offeredItems
                    : (Array.isArray(data.offeredItems) && data.offeredItems.length > 0
                        ? normalizeOfferedItems(data.offeredItems)
                        : prev.offeredItems)
            };
        });
    };

    const buildAutofillPayload = () => ({
        businessName: form.businessName,
        industry: form.industry,
        subIndustry: form.subIndustry,
        description: form.description,
        location: form.location,
        coreServices: form.coreServices,
        projectTypes: form.projectTypes,
        businessModel: form.businessModel,
        primaryWebsiteMode: form.primaryWebsiteMode,
        primaryEntityType: form.primaryEntityType,
        locationFocus: form.locationFocus,
        contentStyleHint: form.contentStyleHint
    });

    const buildCreatePayload = () => ({
        businessName: form.businessName,
        industry: form.industry,
        subIndustry: form.subIndustry,
        description: form.description,

        tone: form.tone,
        themeKey: form.themeKey,

        primaryAudience: form.primaryAudience,
        secondaryAudience: form.secondaryAudience,
        location: form.location,
        serviceAreas: form.serviceAreas,

        goalOfWebsite: form.goalOfWebsite,
        callToActionType: form.callToActionType,
        brandStyle: form.brandStyle,
        pricePositioning: form.pricePositioning,

        coreServices: form.coreServices,
        projectTypes: form.projectTypes,
        uniqueSellingPoints: form.uniqueSellingPoints,
        trustSignals: form.trustSignals,
        yearsInBusiness: form.yearsInBusiness,
        mustIncludeWords: form.mustIncludeWords,
        avoidWords: form.avoidWords,

        competitorWebsiteUrl: form.competitorWebsiteUrl,
        competitorStyleNotes: form.competitorStyleNotes,
        desiredLayoutStyle: form.desiredLayoutStyle,
        visualTone: form.visualTone,
        preferredSectionDensity: form.preferredSectionDensity,
        referenceWebsiteType: form.referenceWebsiteType,

        businessModel: form.businessModel,
        primaryWebsiteMode: form.primaryWebsiteMode,
        primaryEntityType: form.primaryEntityType,
        locationFocus: form.locationFocus,
        contentStyleHint: form.contentStyleHint,

        pages: Array.isArray(form.pages)
            ? form.pages.map((p) => String(p).toLowerCase())
            : [],

        offeredItems: Array.isArray(form.offeredItems)
            ? form.offeredItems.map((item) => ({
                name: item.name,
                subtitle: item.subtitle,
                description: item.description,
                badge: item.badge,
                category: item.category,
                audience: item.audience,
                level: item.level,
                imagePreference: item.imagePreference,
                keyPoints: Array.isArray(item.keyPoints)
                    ? item.keyPoints.filter(Boolean)
                    : []
            }))
            : []
    });

    const loadPreview = async (id) => {
        const res = await getPreview(id);

        setPreviewData(res || null);
        setStatus(res?.status || "");
        setLiveUrl(res?.liveUrl || "");

        // AUTO SYNC VARIANT FROM BACKEND
        if (res?.activeVariant) {
            setVariantKey(res.activeVariant);
        }

        return res;
    };

    const pollProjectStatus = (id) => {
        const interval = setInterval(async () => {
            try {
                const preview = await loadPreview(id);

                if (preview?.status === "Generated") {
                    clearInterval(interval);

                    // reload preview automatically
                    setVariantKey((prev) => prev + "_r");
                }

                if (preview?.status === "Failed") {
                    clearInterval(interval);
                    alert("Website generation failed.");
                }
            } catch (error) {
                clearInterval(interval);
                console.error(error);
            }
        }, 3000);
    };

    const handleAutofill = async () => {
        if (!form.businessName || !form.industry || !form.description) {
            alert("Enter Business Name, Industry, and Description first.");
            return;
        }

        try {
            setAutofilling(true);
            const data = await autofillWebsiteForm(buildAutofillPayload());
            mergeAutofillData(data);
            if (Array.isArray(data?.offeredItems) && data.offeredItems.length > 0) {
                setShowOfferings(true);
            }
            alert("AI autofill completed.");
        } catch (error) {
            console.error(error);
            alert("AI autofill failed.");
        } finally {
            setAutofilling(false);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setPreviewData(null);
            setLiveUrl("");
            setProjectId("");
            setStatus("");

            const payload = buildCreatePayload();
            const res = await createAndQueueProject(payload);

            setProjectId(res?.projectId || "");
            setStatus(res?.status || "Submitted");

            if (res?.projectId) {
                pollProjectStatus(res.projectId);
            }
        } catch (error) {
            console.error(error);
            alert("Failed to save project.");
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async () => {
        if (!projectId) {
            alert("Save project first.");
            return;
        }

        try {
            setLoading(true);
            await loadPreview(projectId);
        } catch (error) {
            console.error(error);
            alert("Failed to load preview.");
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async () => {
        if (!projectId) {
            alert("Save project first.");
            return;
        }

        try {
            setPublishing(true);
            const res = await publishProject(projectId);

            if (res?.liveUrl) {
                setLiveUrl(res?.liveUrl || "");
                await loadPreview(projectId);
                alert("Website published successfully.");
            } else {
                alert("Publish failed.");
            }
        } catch (error) {
            console.error(error);
            alert("Failed to publish website.");
        } finally {
            setPublishing(false);
        }
    };

    const handleRegenerate = async () => {
        if (!projectId) {
            alert("Save project first.");
            return;
        }

        try {
            setRegenerating(true);
            const res = await regenerateProject(projectId);

            if (res) {
                setStatus("Submitted");
                setPreviewData(null);
                setLiveUrl("");
                alert("Regeneration started.");
                pollProjectStatus(projectId);

                // FORCE preview refresh cycle
                setVariantKey("A"); // reset to default
                setPreviewData(null);
            } else {
                alert("Regenerate failed.");
            }
        } catch (error) {
            console.error(error);
            alert("Failed to regenerate website.");
        } finally {
            setRegenerating(false);
        }
    };

    const handleOpenLivePreview = () => {
        const url = previewData?.liveUrl || liveUrl || "";

        if (!url) {
            alert("Live preview URL is not available yet.");
            return;
        }

        window.open(`${url}?v=${variantKey}`, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="wg-page">
            <div className="wg-shell">
                <div className="wg-header">
                    <div>
                        <div className="wg-badge">Shriansh Nexus</div>
                        <h1 className="wg-title">AI Website Generator</h1>
                        <p className="wg-subtitle">
                            Fill your business details, let AI enrich the content, choose a theme,
                            then save, preview, and publish.
                        </p>
                    </div>
                </div>

                <StepProgress step={step} />

                <div className="wg-card">
                    {step === 1 && (
                        <BusinessDetailsStep
                            form={form}
                            updateField={updateField}
                            onAutofill={handleAutofill}
                            autofilling={autofilling}
                            mode={formMode}
                            setMode={setFormMode}
                            showOfferings={showOfferings}
                            setShowOfferings={setShowOfferings}
                            showAdvanced={showAdvanced}
                            setShowAdvanced={setShowAdvanced}
                        />
                    )}

                    {step === 2 && (
                        <ThemeSelectionStep
                            themes={themes}
                            form={form}
                            updateField={updateField}
                        />
                    )}

                    {step === 3 && (
                        <ReviewStep
                            form={form}
                            projectId={projectId}
                            status={status}
                            liveUrl={liveUrl}
                        />
                    )}
                </div>

                <ActionBar
                    step={step}
                    loading={loading}
                    publishing={publishing}
                    regenerating={regenerating}
                    onBack={() => setStep((s) => Math.max(1, s - 1))}
                    onNext={() => setStep((s) => Math.min(3, s + 1))}
                    onSave={handleSave}
                    onPreview={handlePreview}
                    onPublish={handlePublish}
                    onRegenerate={handleRegenerate}
                />

                {previewData && status === "Generated" && (
                    <GeneratedWebsiteExperience
                        project={{
                            ...form,
                            status,
                            liveUrl
                        }}
                        preview={previewData}
                        onPublish={handlePublish}
                        onRegenerate={handleRegenerate}
                        onOpenPreview={handleOpenLivePreview}
                    />
                )}

                {projectId && previewData && (
                    <div className="embedded-preview">
                        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedVariant("A");
                                    refreshPreview();
                                }}
                            >
                                Variant A
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedVariant("B");
                                    refreshPreview();
                                }}
                            >
                                Variant B
                            </button>
                        </div>

                        {projectId && (
                            <iframe
                                key={`${selectedVariant}-${previewRefreshKey}`}
                                src={previewDocumentUrl}
                                title="Website Preview"
                                style={{
                                    width: "100%",
                                    minHeight: "900px",
                                    border: "none",
                                    borderRadius: "20px",
                                    background: "#fff"
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}